import { useTranslation } from 'react-i18next';
// arsenal-tpms/frontend/src/components/schematics/VehicleTelemetryModal/index.jsx
// ✅ VERSIÓN CON SINCRONIZACIÓN BIDIRECCIONAL - Mapa y gráficas se comunican entre sí
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Alert,
    CircularProgress,
    Typography,
    Tabs,
    Tab,
    useTheme,
    useMediaQuery,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Close as CloseIcon,
    Timeline as TimelineIcon,
    TableChart as TableIcon,
    CompareArrows as CompareArrowsIcon,
    Map as MapIcon,
    MapOutlined as MapOutlinedIcon,
    KeyboardArrowLeft as KeyboardArrowLeftIcon,
    KeyboardArrowRight as KeyboardArrowRightIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';

import Highcharts from 'highcharts';
import { LoadScript } from '@react-google-maps/api';

import { useApiCall } from '../../../hooks/useApi';
import { useUserTimezone } from '../../../hooks/useUserTimezone';
import { api } from '../../../services/api';

import VehicleRouteMapModal from '../VehicleRouteMapModal';
import ChartsTab from './ChartsTab';
import TableTab from './TableTab';
import ComparisonTab from './ComparisonTab';

import {
    GOOGLE_MAPS_API_KEY,
    TIME_RANGE_CONFIG,
    HIGHCHARTS_LANG_CONFIG,
    AVAILABLE_TABS
} from './constants';
import { calculateDateRange } from './utils';

const VehicleTelemetryModal = ({
    open,
    vehicle,
    onClose,
    realtimeData,
    // Posicion pulsada en el esquematico: la grafica abre filtrada por ella.
    initialPosition = null
}) => {
  const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // User detection
    const user = { email: t('hardcoded.components_schematics_vehicletelemetrymodal.carlosarsenalmx') };
    const isAdvancedUser = user?.email === 'carlos@arsenal.mx';

    // Timezone system
    const timezoneHook = useUserTimezone();
    const {
        formatUserDateTimeSync,
        timezoneInfo,
        isUserConfigured,
        isBrowserDetected,
        effectiveTimezone
    } = timezoneHook;

    // MAP STATES - El mapa siempre está visible en desktop, solo se colapsa
    const [mapCollapsed, setMapCollapsed] = useState(false);
    const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
    const [hoveredTimestamp, setHoveredTimestamp] = useState(null);
    const [mapClickedTimestamp, setMapClickedTimestamp] = useState(null); // 🆕 Timestamp del punto clickeado en el mapa

    // Unit Preferences State
    const [unitPreferences, setUnitPreferences] = useState(null);
    const [unitPreferencesLoading, setUnitPreferencesLoading] = useState(false);

    // Main states
    const [selectedPositions, setSelectedPositions] = useState(['all']);

    // Al abrir desde una posicion concreta, se enfoca esa serie; si se abre
    // desde el vehiculo (sin posicion), se muestran todas.
    useEffect(() => {
        if (!open) return;
        setSelectedPositions(initialPosition ? [String(initialPosition)] : ['all']);
    }, [open, initialPosition]);
    const [customDateRange, setCustomDateRange] = useState({
        enabled: true,
        startDate: '',
        endDate: ''
    });
    const [selectedTimeRange, setSelectedTimeRange] = useState(t('hardcoded.components_schematics_vehicletelemetrymodal.1day'));
    const [currentTab, setCurrentTab] = useState(0);
    const [alarmProfiles, setAlarmProfiles] = useState([]);

    // Referencias para evitar re-inicializaciones
    const initializedVehicleRef = useRef(null);
    const isAutoSwitchingRef = useRef(false);

    // 🆕 Handler para clicks en el mapa
    const handleMapPointClick = useCallback((timestamp) => {
        console.log('🗺️ Map point clicked:', timestamp);
        setMapClickedTimestamp(timestamp);

        // Limpiar el timestamp después de un momento para permitir múltiples clicks
        setTimeout(() => {
            setMapClickedTimestamp(null);
        }, 100);
    }, []);

    // Inicializar fechas SINCRÓNICAMENTE cuando cambia el vehículo
    useEffect(() => {
        if (vehicle?.id && vehicle.id !== initializedVehicleRef.current) {
            console.log('🚀 Initializing dates for vehicle:', vehicle.id);

            const dates = calculateDateRange(t('hardcoded.components_schematics_vehicletelemetrymodal.1day'), TIME_RANGE_CONFIG);

            setCustomDateRange({
                enabled: true,
                startDate: dates.startDate,
                endDate: dates.endDate
            });

            setSelectedTimeRange(t('hardcoded.components_schematics_vehicletelemetrymodal.1day'));
            setSelectedPositions(['all']);
            setCurrentTab(0);

            initializedVehicleRef.current = vehicle.id;

            console.log('✅ Dates initialized:', dates);
        }
    }, [vehicle?.id]);

    // Cargar unit preferences UNA SOLA VEZ
    useEffect(() => {
        const loadUnitPreferences = async () => {
            if (unitPreferences) return;

            setUnitPreferencesLoading(true);

            try {
                const response = await api.unitPreferences.getMyPreferences();
                setUnitPreferences(response.data);
                console.log(t('hardcoded.components_schematics_vehicletelemetrymodal.unit_preferences_loaded'), response.data);
            } catch (error) {
                console.error('❌ Error loading unit preferences:', error);

                setUnitPreferences({
                    pressure_unit: 'PSI',
                    temperature_unit: 'C',
                    acceleration_unit: 'G',
                    speed_unit: 'km/h',
                    decimal_places: '2',
                    use_imperial_system: false
                });
            } finally {
                setUnitPreferencesLoading(false);
            }
        };

        loadUnitPreferences();
    }, []);

    // Set global Highcharts options
    useEffect(() => {
        if (effectiveTimezone) {
            Highcharts.setOptions({
                time: {
                    useUTC: true,
                    timezone: effectiveTimezone
                },
                lang: HIGHCHARTS_LANG_CONFIG
            });
        }
    }, [effectiveTimezone]);

    // Format smart date time
    const formatSmartDateTime = useCallback((timestamp, includeSeconds = true) => {
        if (!timestamp) return '';

        try {
            const options = {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                timeZoneName: 'short'
            };

            if (includeSeconds) {
                options.second = '2-digit';
            }

            return formatUserDateTimeSync(timestamp, options);
        } catch (error) {
            console.error(t('hardcoded.components_schematics_vehicleroutemapmodal.error_formatting_timestamp'), error, timestamp);
            return new Date(timestamp).toLocaleString();
        }
    }, [formatUserDateTimeSync]);

    const formatDateLocal = useCallback((timestamp) => {
        try {
            return formatUserDateTimeSync(timestamp, {
                year: 'numeric',
                month: 'short',
                day: '2-digit'
            });
        } catch (error) {
            return new Date(timestamp).toLocaleDateString();
        }
    }, [formatUserDateTimeSync]);

    const formatTimeLocal = useCallback((timestamp) => {
        try {
            return formatUserDateTimeSync(timestamp, {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
        } catch (error) {
            return new Date(timestamp).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
        }
    }, [formatUserDateTimeSync]);

    const formatDayMonthLocal = useCallback((timestamp) => {
        try {
            return formatUserDateTimeSync(timestamp, {
                day: '2-digit',
                month: '2-digit'
            });
        } catch (error) {
            const date = new Date(timestamp);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            return `${day}/${month}`;
        }
    }, [formatUserDateTimeSync]);

    const formatDateTimeLocal = useCallback((timestamp) => {
        return formatSmartDateTime(timestamp, true);
    }, [formatSmartDateTime]);

    const calculateDateRangeCallback = useCallback((rangeType) => {
        return calculateDateRange(rangeType, TIME_RANGE_CONFIG);
    }, []);

    // fetchParams más estable
    const fetchParams = useMemo(() => {
        if (!vehicle?.id || !customDateRange.startDate || !customDateRange.endDate) {
            return null;
        }

        const params = {
            vehicleId: vehicle.id,
            timeRange: selectedTimeRange,
            startDate: customDateRange.startDate,
            endDate: customDateRange.endDate,
            enabled: customDateRange.enabled
        };

        console.log(t('hardcoded.components_schematics_vehicletelemetrymodal_index.fetchparams_created'), params);
        return params;
    }, [vehicle?.id, selectedTimeRange, customDateRange.startDate, customDateRange.endDate, customDateRange.enabled]);

    // Resumen de lo que entregó el backend: cuántas lecturas había y cuántas
    // caben. Lo muestra la pestaña de gráficas.
    const [telemetryMeta, setTelemetryMeta] = useState({ devueltas: null, disponibles: null, reducida: false });

    // useApiCall SIN open en dependencias
    const {
        data: telemetryData,
        loading: telemetryLoading,
        refetch: refetchTelemetry
    } = useApiCall(() => {
        if (!fetchParams) {
            console.log(t('hardcoded.components_schematics_vehicletelemetrymodal_index.skipping_telemetry_fetch_-_no'));
            return null;
        }

        console.log('🚀 Fetching telemetry with params:', fetchParams);

        const params = {
            vehicle_id: fetchParams.vehicleId,
            limit: 50000
        };

        if (fetchParams.enabled && fetchParams.startDate && fetchParams.endDate) {
            if (fetchParams.timeRange === '1hour') {
                const now = new Date();
                const oneHourAgo = new Date(now.getTime() - (60 * 60 * 1000));
                params.start_date = oneHourAgo.toISOString();
                params.end_date = now.toISOString();
            } else {
                params.start_date = new Date(fetchParams.startDate + 'T00:00:00.000Z').toISOString();
                params.end_date = new Date(fetchParams.endDate + 'T23:59:59.999Z').toISOString();
            }
        }

        // El backend informa por cabeceras si hubo que reducir la resolución.
        // `useApi` se queda solo con `response.data`, así que se leen aquí y se
        // devuelve la misma respuesta: sin esto, una gráfica recortada se
        // presentaría como si fuera el dato completo.
        return api.telemetry.getData(params).then((respuesta) => {
            const leidas = Number(respuesta?.headers?.['x-telemetry-returned']);
            const disponibles = Number(respuesta?.headers?.['x-telemetry-available']);
            setTelemetryMeta({
                devueltas: Number.isFinite(leidas) ? leidas : null,
                disponibles: Number.isFinite(disponibles) ? disponibles : null,
                reducida: respuesta?.headers?.['x-telemetry-reduced'] === '1',
            });
            return respuesta;
        });
    }, [fetchParams, calculateDateRangeCallback]);

    // Load alarm profiles
    useEffect(() => {
        if (!vehicle?.id) {
            setAlarmProfiles([]);
            return;
        }

        const loadProfiles = async () => {
            try {
                const response = await api.alarmProfiles.getAssignments({
                    vehicle_id: vehicle.id,
                    active_only: true
                });

                setAlarmProfiles(response.data || []);
            } catch (error) {
                console.error(t('hardcoded.components_schematics_vehicletelemetrymodal.alarmprofiles_load_error'), error);
                setAlarmProfiles([]);
            }
        };

        loadProfiles();
    }, [vehicle?.id]);

    // Reset cuando cierra el modal
    useEffect(() => {
        if (!open) {
            setHoveredTimestamp(null);
            setMapClickedTimestamp(null);
        }
    }, [open]);

    const availableTabs = [
        { label: '', icon: <TimelineIcon />, component: 'charts' }
    ];

    if (isAdvancedUser) {
        availableTabs.push(
            { label: '', icon: <TableIcon />, component: 'table' },
            { label: '', icon: <CompareArrowsIcon />, component: 'comparison' }
        );
    }

    if (!vehicle) return null;

    return (
        <LoadScript
            googleMapsApiKey={GOOGLE_MAPS_API_KEY}
            onLoad={() => setGoogleMapsLoaded(true)}
            onError={(error) => console.error('Error loading Google Maps:', error)}
        >
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xl"
                fullWidth
                fullScreen={isSmallMobile}
                PaperProps={{
                    sx: {
                        borderRadius: { xs: 0, sm: 2 },
                        height: { xs: '100vh', sm: '95vh' },
                        maxHeight: { xs: '100vh', sm: '95vh' },
                        maxWidth: '1800px',
                        m: { xs: 0, sm: 2 }
                    }
                }}
            >
                <DialogContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {unitPreferencesLoading && !unitPreferences && (
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 10000,
                            bgcolor: 'background.paper',
                            p: 2
                        }}>
                            <Alert severity="info" icon={<CircularProgress size={20} />}>
                                {t('hardcoded.components_schematics_vehicletelemetrymodal_index.cargando_preferencias_de_unidades')}</Alert>
                        </Box>
                    )}

                    {/* Tabs */}
                    <Box sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        px: { xs: 1, sm: 2 },
                        py: 0.5,
                        bgcolor: 'background.paper',
                        zIndex: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Tabs
                            value={currentTab}
                            onChange={(e, newValue) => setCurrentTab(newValue)}
                            variant={isMobile ? "scrollable" : "fullWidth"}
                            scrollButtons={isMobile ? "auto" : false}
                            allowScrollButtonsMobile
                            sx={{
                                minHeight: { xs: 48, sm: 40 },
                                '& .MuiTab-root': {
                                    minHeight: { xs: 48, sm: 40 },
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    fontWeight: 600,
                                    px: { xs: 1, sm: 2 },
                                    py: { xs: 1.5, sm: 1 },
                                    '&.Mui-selected': {
                                        color: 'primary.main',
                                        bgcolor: 'primary.50'
                                    }
                                },
                                '& .MuiTabs-indicator': {
                                    height: 3
                                }
                            }}
                        >
                            {availableTabs.map((tab, index) => (
                                <Tab
                                    key={index}
                                    label={isSmallMobile ? tab.icon : tab.label}
                                    icon={!isSmallMobile ? React.cloneElement(tab.icon, { sx: { fontSize: '18px' } }) : null}
                                    iconPosition={isSmallMobile ? "start" : "top"}
                                    sx={{
                                        minWidth: { xs: 80, sm: 120 },
                                        textTransform: 'none'
                                    }}
                                />
                            ))}
                        </Tabs>
                    </Box>

                    {/* Contenido de tabs */}
                    <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        {currentTab === 0 && (
                            <Box sx={{
                                display: 'flex',
                                height: '100%',
                                flexDirection: isMobile ? 'column' : 'row',
                                overflow: 'hidden'
                            }}>
                                {/* Charts Tab Component */}
                                <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                    <ChartsTab
                                        vehicle={vehicle}
                                        telemetryData={telemetryData}
                                        telemetryMeta={telemetryMeta}
                                        telemetryLoading={telemetryLoading}
                                        selectedPositions={selectedPositions}
                                        setSelectedPositions={setSelectedPositions}
                                        customDateRange={customDateRange}
                                        setCustomDateRange={setCustomDateRange}
                                        selectedTimeRange={selectedTimeRange}
                                        setSelectedTimeRange={setSelectedTimeRange}
                                        refetchTelemetry={refetchTelemetry}
                                        alarmProfiles={alarmProfiles}
                                        unitPreferences={unitPreferences}
                                        formatDateTimeLocal={formatDateTimeLocal}
                                        formatDateLocal={formatDateLocal}
                                        formatTimeLocal={formatTimeLocal}
                                        formatDayMonthLocal={formatDayMonthLocal}
                                        effectiveTimezone={effectiveTimezone}
                                        calculateDateRange={calculateDateRangeCallback}
                                        setHoveredTimestamp={setHoveredTimestamp}
                                        mapClickedTimestamp={mapClickedTimestamp}
                                    />
                                </Box>

                                {/* Right panel - GPS map SIEMPRE VISIBLE en desktop */}
                                {!isMobile && googleMapsLoaded && (
                                    <Box
                                        sx={{
                                            width: mapCollapsed ? '60px' : '400px',
                                            minWidth: mapCollapsed ? '60px' : '400px',
                                            flexShrink: 0,
                                            borderLeft: 1,
                                            borderColor: 'divider',
                                            bgcolor: 'background.default',
                                            transition: 'all 0.3s',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            overflow: 'hidden',
                                            cursor: mapCollapsed ? 'pointer' : 'default'
                                        }}
                                        onClick={() => {
                                            if (mapCollapsed) {
                                                setMapCollapsed(false);
                                            }
                                        }}
                                    >
                                        <Box sx={{
                                            p: 1.5,
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            bgcolor: 'background.paper'
                                        }}>
                                            {!mapCollapsed && (
                                                <Typography variant="subtitle2" fontWeight="bold">
                                                    {t("hardcoded.components_schematics_vehicletelemetrymodal_index.mapa_gps")}
                                                </Typography>
                                            )}
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Tooltip title={mapCollapsed ? "Expandir mapa" : t('hardcoded.components_schematics_vehicletelemetrymodal_index.colapsar_mapa')}>
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setMapCollapsed(!mapCollapsed);
                                                        }}
                                                        size="small"
                                                    >
                                                        {mapCollapsed ? <KeyboardArrowLeftIcon /> : <KeyboardArrowRightIcon />}
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>

                                        {!mapCollapsed && (
                                            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                                <VehicleRouteMapModal
                                                    vehicle={vehicle}
                                                    dateRange={{
                                                        startDate: customDateRange.startDate,
                                                        endDate: customDateRange.endDate
                                                    }}
                                                    hoveredTimestamp={hoveredTimestamp}
                                                    formatUserDateTimeSync={formatUserDateTimeSync}
                                                    effectiveTimezone={effectiveTimezone}
                                                    isVisible={!mapCollapsed}
                                                    onMapPointClick={handleMapPointClick}
                                                />
                                            </Box>
                                        )}

                                        {mapCollapsed && (
                                            <Box sx={{
                                                flex: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 1
                                            }}>
                                                <MapOutlinedIcon
                                                    sx={{
                                                        fontSize: 32,
                                                        color: 'text.disabled',
                                                        transform: 'rotate(90deg)'
                                                    }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        writingMode: 'vertical-rl',
                                                        textOrientation: 'mixed',
                                                        color: 'text.secondary',
                                                        fontSize: '0.7rem'
                                                    }}
                                                >
                                                    {t("hardcoded.components_schematics_vehicletelemetrymodal_index.mapa_gps")}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {/* Mobile: comportamiento sin cambios */}
                                {isMobile && googleMapsLoaded && !mapCollapsed && (
                                    <Box sx={{
                                        width: '100%',
                                        height: '300px',
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        bgcolor: 'background.default',
                                        transition: 'all 0.3s',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        overflow: 'hidden'
                                    }}>
                                        <Box sx={{
                                            p: 1,
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            bgcolor: 'background.paper'
                                        }}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
                                                {t("hardcoded.components_schematics_vehicletelemetrymodal_index.mapa_gps")}
                                            </Typography>
                                            <IconButton
                                                onClick={() => setMapCollapsed(true)}
                                                size="small"
                                            >
                                                <KeyboardArrowDownIcon />
                                            </IconButton>
                                        </Box>

                                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                            <VehicleRouteMapModal
                                                vehicle={vehicle}
                                                dateRange={{
                                                    startDate: customDateRange.startDate,
                                                    endDate: customDateRange.endDate
                                                }}
                                                hoveredTimestamp={hoveredTimestamp}
                                                formatUserDateTimeSync={formatUserDateTimeSync}
                                                effectiveTimezone={effectiveTimezone}
                                                isVisible={!mapCollapsed}
                                                onMapPointClick={handleMapPointClick}
                                            />
                                        </Box>
                                    </Box>
                                )}

                                {/* Mobile: botón flotante para expandir mapa cuando está colapsado */}
                                {isMobile && googleMapsLoaded && mapCollapsed && (
                                    <Box
                                        sx={{
                                            position: 'fixed',
                                            bottom: 80,
                                            right: 16,
                                            zIndex: 1000
                                        }}
                                    >
                                        <Tooltip title={t('hardcoded.components_schematics_chartsview.mostrar_mapa_gps')}>
                                            <IconButton
                                                onClick={() => setMapCollapsed(false)}
                                                sx={{
                                                    bgcolor: 'primary.main',
                                                    color: 'white',
                                                    width: 56,
                                                    height: 56,
                                                    boxShadow: 3,
                                                    '&:hover': {
                                                        bgcolor: 'primary.dark',
                                                    }
                                                }}
                                            >
                                                <MapIcon sx={{ fontSize: 28 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {currentTab === 1 && isAdvancedUser && (
                            <TableTab
                                vehicle={vehicle}
                                telemetryData={telemetryData}
                                telemetryLoading={telemetryLoading}
                                refetchTelemetry={refetchTelemetry}
                                unitPreferences={unitPreferences}
                                formatDateTimeLocal={formatDateTimeLocal}
                                timezoneInfo={timezoneInfo}
                            />
                        )}

                        {currentTab === 2 && isAdvancedUser && (
                            <ComparisonTab
                                vehicle={vehicle}
                                formatSmartDateTime={formatSmartDateTime}
                                timezoneInfo={timezoneInfo}
                            />
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Button onClick={onClose} variant="contained">{t('common.close')}</Button>
                </DialogActions>
            </Dialog>
        </LoadScript>
    );
};

export default VehicleTelemetryModal;